import { ServiceTitanIconLink } from "@/core/components/service-titan-icon-link";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import type { ApAgentAction } from "@/accounts-payable/types";
import {
  actionCategoryLabel,
  actionDetail,
  actionOutcomeCopy,
  createdPurchaseOrderId,
  isNoOpAction,
  isPendingReviewAction,
  stPurchaseOrderUrl,
} from "@/accounts-payable/utils";
import { ActionStatusGlyph, ConfidenceLabel } from "./shared";
import { StChangesNote } from "./st-changes-note";

/**
 * An action row's status glyph, in a box the height of the title's 16px line
 * so the 20px glyph centres on it rather than hanging 2px low.
 */
export const ActionRowGlyph = ({ action }: { action: ApAgentAction }) => (
  <Stack
    gap={4}
    direction="row"
    align="center"
    justify="center"
    className="h-4 w-5 flex-none"
  >
    <ActionStatusGlyph action={action} outline className="size-5 flex-none" />
  </Stack>
);

/**
 * The top of an action row, shared by the invoice sidebar and the review page:
 * the category as the title with the confidence (pending) on the right, then
 * the target and any outcome. A flag's category says little, so its reason is
 * the title and the category drops to a caption. Only an apply failure reads
 * red (the Automated tab's rule).
 */
export const ActionRowHeader = ({
  action,
  showTarget,
}: {
  action: ApAgentAction;
  /** False where the row shows the target elsewhere (the review page's
   * ServiceTitan card), so it isn't said twice. */
  showTarget: boolean;
}) => {
  const pending = isPendingReviewAction(action);
  const noOp = isNoOpAction(action);
  const detail = actionDetail(action);
  const category = actionCategoryLabel(action);
  const outcome = actionOutcomeCopy(action);
  const target = showTarget && !noOp ? detail : null;
  // A created PO has no number until it lands, so the link joins the target
  // only then.
  const createdPoId = showTarget ? createdPurchaseOrderId(action) : null;

  return (
    <Stack className="w-full" align="start" gap={1}>
      <Stack
        className="w-full"
        direction="row"
        justify="between"
        align="start"
        gap={4}
      >
        <Text variant="label">{noOp ? detail : category}</Text>
        {noOp ? (
          <Text
            variant="caption"
            tone="muted"
            className="flex-none whitespace-nowrap"
          >
            {category}
          </Text>
        ) : (
          pending && (
            <ConfidenceLabel
              value={action.confidence}
              className="flex-none whitespace-nowrap"
            />
          )
        )}
      </Stack>
      {(target || createdPoId || outcome) && (
        <Text tone="muted">
          {target}
          {createdPoId && (
            <>
              {target && " · "}
              <ServiceTitanIconLink
                url={stPurchaseOrderUrl(createdPoId)}
                label={`Open purchase order #${createdPoId} in ServiceTitan`}
              >
                PO #{createdPoId}
              </ServiceTitanIconLink>
            </>
          )}
          {outcome && (
            <span
              className={cn({
                "text-danger": action.status === "apply_failed",
                "text-subtle": action.status !== "apply_failed",
              })}
            >
              {(target || createdPoId) && " · "}
              {outcome}
            </span>
          )}
        </Text>
      )}
      <StChangesNote action={action} className="mt-0" />
    </Stack>
  );
};
